import { useState, useEffect } from 'react'
import { X, Loader2, Sparkles } from 'lucide-react'
import clsx from 'clsx'
import { analyzerApi, PromptTemplate } from '@/services/api'

interface ReanalyzeModalProps {
  analysisId: string
  elementType: 'background' | 'character' | 'title' | 'subtitle' | 'author' | 'publisher'
  elementLabel: string
  onClose: () => void
  onSuccess: () => void
}

const AI_MODELS = [
  { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash', description: '빠른 분석, 일반 용도' },
  { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', description: '고품질 분석, 상세한 결과' },
  { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', description: '균형 잡힌 속도와 품질' },
  { id: 'claude-sonnet', name: 'Claude Sonnet', description: '정교한 분석' },
]

export function ReanalyzeModal({
  analysisId,
  elementType,
  elementLabel,
  onClose,
  onSuccess
}: ReanalyzeModalProps) {
  const [templates, setTemplates] = useState<PromptTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')
  const [customPrompt, setCustomPrompt] = useState<string>('')
  const [useCustomPrompt, setUseCustomPrompt] = useState(false)
  const [selectedModel, setSelectedModel] = useState('gemini-2.0-flash')
  const [isLoading, setIsLoading] = useState(false)
  const [isReanalyzing, setIsReanalyzing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load templates
  useEffect(() => {
    loadTemplates()
  }, [elementType])

  const loadTemplates = async () => {
    setIsLoading(true)
    try {
      const result = await analyzerApi.listTemplates()
      if (result.success && result.data) {
        // Filter templates for this element type or 'all'
        const dbKey = elementType === 'title' ? 'typography' : elementType
        const filtered = result.data.filter(
          t => t.element_type === dbKey || t.element_type === 'all'
        )
        setTemplates(filtered)

        // Set default template
        if (filtered.length > 0) {
          const systemDefault = filtered.find(t => t.is_system && t.name.includes('기본'))
          setSelectedTemplate(systemDefault?.id || filtered[0].id)
        }
      }
    } catch (err) {
      console.error('Failed to load templates:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleReanalyze = async () => {
    setIsReanalyzing(true)
    setError(null)

    try {
      const result = await analyzerApi.reanalyzeElement(analysisId, elementType, {
        templateId: useCustomPrompt ? undefined : selectedTemplate,
        customPrompt: useCustomPrompt ? customPrompt : undefined,
        model: selectedModel
      })

      if (result.success) {
        onSuccess()
      } else {
        setError(result.error?.message || '재분석에 실패했습니다.')
      }
    } catch (err) {
      console.error('Reanalyze failed:', err)
      setError('재분석 중 오류가 발생했습니다.')
    } finally {
      setIsReanalyzing(false)
    }
  }

  const selectedTemplateObj = templates.find(t => t.id === selectedTemplate)

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-bg-primary rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-bg-primary border-b border-border-subtle p-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">{elementLabel} 재분석</h2>
            <p className="text-sm text-text-tertiary mt-1">
              프롬프트 템플릿을 선택하거나 직접 입력하여 재분석합니다
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-bg-elevated rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Prompt Mode Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setUseCustomPrompt(false)}
              className={clsx(
                'flex-1 py-2 px-4 rounded-lg border transition-colors',
                !useCustomPrompt
                  ? 'bg-accent-primary/10 border-accent-primary text-accent-primary'
                  : 'border-border-default hover:border-border-hover'
              )}
            >
              템플릿 사용
            </button>
            <button
              onClick={() => setUseCustomPrompt(true)}
              className={clsx(
                'flex-1 py-2 px-4 rounded-lg border transition-colors',
                useCustomPrompt
                  ? 'bg-accent-primary/10 border-accent-primary text-accent-primary'
                  : 'border-border-default hover:border-border-hover'
              )}
            >
              직접 입력
            </button>
          </div>

          {/* Template Selection */}
          {!useCustomPrompt ? (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-text-secondary">
                추출용 프롬프트 템플릿
              </label>
              {isLoading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-text-tertiary" />
                  <p className="text-sm text-text-tertiary mt-2">템플릿 로딩 중...</p>
                </div>
              ) : (
                <>
                  <select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="input w-full"
                  >
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} {template.is_system ? '(시스템)' : ''}
                      </option>
                    ))}
                  </select>

                  {/* Template Preview */}
                  {selectedTemplateObj && (
                    <div className="bg-bg-elevated border border-border-subtle rounded-lg p-4">
                      <p className="text-xs text-text-tertiary mb-2">프롬프트 미리보기:</p>
                      <pre className="text-xs text-text-secondary whitespace-pre-wrap font-mono">
                        {selectedTemplateObj.template_text.slice(0, 500)}
                        {selectedTemplateObj.template_text.length > 500 && '...'}
                      </pre>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-text-secondary">
                커스텀 프롬프트
              </label>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="분석 프롬프트를 직접 입력하세요..."
                className="input w-full min-h-[200px] font-mono text-sm"
              />
            </div>
          )}

          {/* Model Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-text-secondary">
              분석 모델
            </label>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="input w-full"
            >
              {AI_MODELS.map(model => (
                <option key={model.id} value={model.id}>
                  {model.name} - {model.description}
                </option>
              ))}
            </select>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-accent-error/10 border border-accent-error/30 rounded-lg p-4 text-sm text-accent-error">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-bg-primary border-t border-border-subtle p-4 flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isReanalyzing}
            className="btn btn-secondary"
          >
            취소
          </button>
          <button
            onClick={handleReanalyze}
            disabled={isReanalyzing || (!useCustomPrompt && !selectedTemplate) || (useCustomPrompt && !customPrompt.trim())}
            className="btn btn-primary"
          >
            {isReanalyzing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                재분석 중...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                재분석 실행
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
